import { useState } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import {
  ShareableRecord,
  useUpdateRecordShares,
} from "../../data/useUpdateRecordShares";
import { showError, showSuccess } from "../../utils/error-handling";
import { AccessButton } from "./AccessButton";
import { AccessPanel } from "./AccessPanel";
import { buildShares, NamedUnit, NewShare, Share, shareIdLists } from "./types";

/**
 * Permission pair gating sharing per record (UX only — the backend still
 * enforces). Screening records are "Checks" mandates, so they share the
 * mandate pair.
 */
const SHARE_PERMISSIONS: Record<SharingModule, [string, string]> = {
  profile: ["profile.add_division", "profile.add_team"],
  application: ["mandate.add_division", "mandate.add_team"],
  screening: ["mandate.add_division", "mandate.add_team"],
  monitoring: ["monitoring.add_division", "monitoring.add_team"],
};

const RECORD_FOR_MODULE: Record<SharingModule, ShareableRecord> = {
  profile: "profile",
  application: "mandate",
  screening: "mandate",
  monitoring: "monitoring",
};

export type SharingModule = "profile" | "application" | "screening" | "monitoring";

interface Props {
  module: SharingModule;
  /** Id of the record that stores the tagging (the Mandate for applications/screening). */
  recordId?: number;
  recordName: string;
  recordReference?: string | null;
  /** "profile", "application", "check", "monitoring record" — used in copy. */
  noun: string;
  primaryDivision?: NamedUnit | null;
  primaryTeam?: NamedUnit | null;
  additionalDivisions?: NamedUnit[] | null;
  additionalTeams?: NamedUnit[] | null;
  /** Queries whose data carries these fields — refetched after a change. */
  invalidateKeys: QueryKey[];
}

/**
 * Header Share button + Sharing panel for one record, wired to save. Drop it
 * into a detail page's header actions; the panel is a portal, so placement
 * only affects where the button sits.
 */
export const RecordSharing = ({
  module,
  recordId,
  recordName,
  recordReference,
  noun,
  primaryDivision,
  primaryTeam,
  additionalDivisions,
  additionalTeams,
  invalidateKeys,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useUpdateRecordShares();

  const shares = buildShares(additionalDivisions, additionalTeams);
  const canShare = SHARE_PERMISSIONS[module].some(hasPermission);

  const save = (next: Share[], successMessage: string) => {
    if (!recordId) return;
    mutate(
      { record: RECORD_FOR_MODULE[module], recordId, ...shareIdLists(next) },
      {
        onSuccess: () => {
          invalidateKeys.forEach((key) => queryClient.invalidateQueries(key));
          showSuccess(successMessage);
        },
        onError: (e) => showError(e, "Failed to update sharing"),
      },
    );
  };

  const handleAdd = (added: NewShare[]) => {
    const count = (kind: NewShare["kind"], word: string) => {
      const n = added.filter((s) => s.kind === kind).length;
      return n ? `${n} ${word}${n === 1 ? "" : "s"}` : "";
    };
    save(
      [...shares, ...added.map(({ kind, id }) => ({ kind, id, name: "" }))],
      `Shared with ${[count("division", "division"), count("team", "team")]
        .filter(Boolean)
        .join(" and ")}`,
    );
  };

  // A Division arrives with its shared Teams, so they're removed together.
  const handleRemove = (removed: Share[]) =>
    save(
      shares.filter(
        (s) => !removed.some((r) => r.kind === s.kind && r.id === s.id),
      ),
      `${removed[0].name}${
        removed.length > 1 ? " and its teams" : ""
      } no longer ${removed.length > 1 ? "have" : "has"} access`,
    );

  return (
    <>
      <AccessButton
        shareCount={shares.length}
        canShare={canShare}
        isActive={isOpen}
        onClick={() => setIsOpen(true)}
      />
      {/* No onChangeLevel / supportsLevels: the API doesn't store an access
          level on shares yet. */}
      <AccessPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        recordName={recordName}
        recordReference={recordReference ?? undefined}
        noun={noun}
        primaryDivision={primaryDivision}
        primaryTeam={primaryTeam}
        shares={shares}
        canShare={canShare}
        isSaving={isLoading}
        onAdd={recordId ? handleAdd : undefined}
        onRemove={recordId ? handleRemove : undefined}
      />
    </>
  );
};
